<template>
  <div class="lead-form-container">
    <div v-if="loading" class="text-center">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <v-card v-else-if="error || !studios || studios.length === 0" class="mx-auto pa-8 text-center" elevation="4" rounded="lg">
      <v-icon size="60" color="error">mdi-alert-circle-outline</v-icon>
      <v-card-title class="justify-center text-h5 font-weight-bold mt-4">Seite nicht gefunden</v-card-title>
      <v-card-text>Leider konnten wir die benötigten Informationen nicht laden. Bitte versuche es später erneut.</v-card-text>
      <v-btn class="mt-5" color="primary" large @click="toHomepage">Zur Startseite</v-btn>
    </v-card>

    <v-card :style="$vuetify.breakpoint.smAndDown ? 'min-height: calc(100vh - 185px)' : ''" v-else class="mx-auto" elevation="4" rounded="lg">
      <div style="max-width: 600px; margin: auto">
        <div v-if="submitted" class="pa-8 text-center">
          <v-icon size="80" color="success">mdi-check-circle-outline</v-icon>
          <h3 class="text-h5 font-weight-bold mt-6 mb-3">Anfrage erhalten!</h3>
          <p class="body-1">Vielen Dank! Wir haben deine Anfrage erhalten und werden uns so schnell wie möglich bei dir melden.</p>
          <v-btn class="mt-5" color="primary" @click="toHomepage">Zurück zur Startseite</v-btn>
        </div>

        <div v-else :style="$vuetify.breakpoint.smAndDown ? 'min-height: calc(100vh - 185px)' : ''">
          <div style="background-color: #212121; border-bottom: 7px solid var(--v-primary)">
            <v-card-title style="font-weight: 1000 !important; font-style: italic" class="white--text justify-center text-h5 font-weight-bold pt-6">
              <div style="height: 100px; width: 100px">
                <v-img height="100px" style="width: 100px" contain src="https://app.wellcomefit.ch/img/logos/socialMedia/logo.png"></v-img>
              </div>
              BRING A FRIEND</v-card-title
            >
          </div>
          <v-img v-if="leadSettings && leadSettings.publicPicture" :src="'/img' + leadSettings.publicPicture"></v-img>

          <div class="text-center mt-5 mx-5" style="font-style: italic; font-size: 0.8rem">Lass dich von uns für weitere Infos kontaktieren.</div>

          <v-card-text class="pa-4">
            <v-form v-model="isFormValid" ref="leadForm">
              <v-select
                v-if="studios.length > 1"
                v-model="selectedStudio"
                :items="studios"
                item-text="name"
                return-object
                label="Wähle dein Studio*"
                outlined
                prepend-inner-icon="mdi-domain"
                class="mb-3"
                :rules="[rules.required]"></v-select>

              <v-text-field
                :value="referralMail"
                label="Weiterempfehlung von"
                outlined
                readonly
                disabled
                prepend-inner-icon="mdi-account-check-outline"
                class="mb-3"></v-text-field>

              <div v-if="leadSettings">
                <v-row no-gutters>
                  <v-col
                    :class="{ 'pr-1': $vuetify.breakpoint.mdAndUp }"
                    :cols="leadSettings.leadFields.surname && $vuetify.breakpoint.mdAndUp ? 6 : 12">
                    <v-text-field
                      v-if="leadSettings.leadFields.name"
                      v-model="leadInformation.name"
                      label="Vorname*"
                      :rules="[rules.required]"
                      outlined
                      prepend-inner-icon="mdi-account-outline"></v-text-field>
                  </v-col>
                  <v-col
                    :class="{ 'pl-1': $vuetify.breakpoint.mdAndUp }"
                    :cols="$vuetify.breakpoint.mdAndUp ? 6 : 12"
                    v-if="leadSettings.leadFields.surname">
                    <v-text-field v-model="leadInformation.surname" label="Nachname*" :rules="[rules.required]" outlined></v-text-field>
                  </v-col>
                </v-row>

                <v-text-field
                  v-if="leadSettings.leadFields.phone"
                  v-model="leadInformation.phone"
                  label="Telefonnummer*"
                  :rules="[rules.required]"
                  outlined
                  prepend-inner-icon="mdi-phone-outline"></v-text-field>

                <v-text-field
                  v-if="leadSettings.leadFields.email"
                  v-model="leadInformation.email"
                  label="E-Mail*"
                  :rules="[rules.required, rules.email]"
                  outlined
                  prepend-inner-icon="mdi-email-outline"></v-text-field>

                <v-textarea
                  v-if="leadSettings.leadFields.message"
                  v-model="leadInformation.message"
                  label="Deine Nachricht"
                  outlined
                  rows="3"
                  no-resize
                  prepend-inner-icon="mdi-message-text-outline"></v-textarea>
              </div>
              <div v-else class="text-center">
                <v-alert color="primary" type="info" text border="left">Bitte wähle ein Studio aus, um fortzufahren.</v-alert>
              </div>

              <div
                v-if="leadSettings && leadSettings.publicConditions"
                class="conditions-container text-caption grey--text text--darken-1"
                v-html="leadSettings.publicConditions"></div>
            </v-form>

            <div v-if="leadSettings && leadSettings.showContactInfo" class="mt-6 text-center">
              <v-divider class="mb-4"></v-divider>
              <p class="mb-2 font-weight-medium">Oder kontaktiere uns direkt:</p>
              <v-btn v-if="selectedStudio.telefon" :href="'tel:' + selectedStudio.telefon" text color="primary" class="lowercase">
                <v-icon left>mdi-phone</v-icon>
                {{ selectedStudio.telefon }}
              </v-btn>
              <v-btn v-if="selectedStudio.email" :href="'mailto:' + selectedStudio.email" text color="primary" class="lowercase">
                <v-icon left>mdi-email</v-icon>
                {{ selectedStudio.email }}
              </v-btn>
            </div>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0">
            <v-btn :disabled="!isFormValid || !selectedStudio" :loading="submitting" @click="sendLead" color="primary" x-large block rounded>
              Absenden
              <v-icon right>mdi-send</v-icon>
            </v-btn>
          </v-card-actions>
        </div>
      </div>
      <div style="background-color: #212121; border-top: 7px solid var(--v-primary)">
        <div style="max-width: 300px" class="d-flex mx-auto my-5">
          <v-img
            class="cursor-pointer"
            height="50px"
            contain
            width="50px"
            @click="openLinkNewWindow('https://www.facebook.com/wellcomefitag/')"
            :src="facebook"></v-img>

          <v-img
            class="cursor-pointer"
            height="50px"
            width="50px"
            contain
            @click="openLinkNewWindow('https://www.instagram.com/wellcomefit')"
            :src="instagram"></v-img>

          <v-img
            class="cursor-pointer"
            height="50px"
            contain
            width="50px"
            @click="openLinkNewWindow('https://www.tiktok.com/@wellcomefit')"
            :src="tiktok"></v-img>
        </div>
        <v-card-text class="mt-3 text-center white--text">© {{ currentYear }} All rights reserved.</v-card-text>
        <div class="d-flex flex-row justify-center mt-2">
          <v-btn color="white" small @click="openLinkNewWindow('https://wellcomefit.ch/impressum')" text>impressum</v-btn>
          <v-btn color="white" small @click="openLinkNewWindow('https://wellcomefit.ch/datenschutz')" text>Datenschutz</v-btn>
          <v-btn color="white" small @click="openLinkNewWindow('https://wellcomefit.ch/agb')" text>AGB</v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import api from "../../../../api"
import { mapActions } from "vuex"

export default {
  data() {
    return {
      // Zustände
      loading: true,
      submitting: false,
      submitted: false,
      error: false,
      isFormValid: false,
      facebook: "https://app.wellcomefit.ch/img/logos/socialMedia/facebook.png",
      tiktok: "https://app.wellcomefit.ch/img/logos/socialMedia/tiktok.png",
      instagram: "https://app.wellcomefit.ch/img/logos/socialMedia/instagram.png",
      // Daten
      studios: [],
      selectedStudio: null,
      leadInformation: {
        name: "",
        surname: "",
        phone: "",
        email: "",
        message: "",
      },

      // Validierungsregeln
      rules: {
        required: (value) => !!value || "Dieses Feld ist erforderlich.",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return !value || pattern.test(value) || "Ungültige E-Mail-Adresse."
        },
      },
    }
  },
  async created() {
    this.setNavigationbarVisibility(false)
    await this.init()
  },
  beforeDestroy() {
    this.setNavigationbarVisibility(true)
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    openLink(link) {
      this.$router.push(link)
      this.scrollToTop()
    },
    openLinkNewWindow(link) {
      window.open(link, "_blank")
    },
    toHomepage() {
      window.location.href = "/"
    },
    async init() {
      try {
        const leadSettingsData = await api.fetchReferralSettingsPublic()
        this.studios = leadSettingsData

        // Wenn es nur ein Studio gibt, dieses direkt auswählen
        if (this.studios && this.studios.length === 1) {
          this.selectedStudio = this.studios[0]
        }
      } catch (err) {
        this.error = true
        console.error("Fehler beim Laden der Studio-Einstellungen:", err)
      } finally {
        this.loading = false
      }
    },
    async sendLead() {
      // Nutzt die Vuetify-interne Validierung
      this.$refs.leadForm.validate()
      if (!this.isFormValid || !this.selectedStudio) {
        this.$toast.error("Bitte fülle alle Pflichtfelder aus.")
        return
      }

      this.submitting = true
      try {
        const leadPayload = {
          ...this.leadInformation,
          studio: this.selectedStudio._id,
        }
        await api.createReferralLeadPublic(this.leadSettings._id, leadPayload, this.selectedStudio, this.referralMail)
        this.submitted = true
        this.$toast.success("Deine Anfrage wurde erfolgreich versendet.")
      } catch (e) {
        this.error = true
        this.$toast.error("Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.")
      } finally {
        this.submitting = false
      }
    },
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },

    // Leitet die Einstellungen vom ausgewählten Studio ab. Das vereinfacht das Template.
    leadSettings() {
      if (this.selectedStudio && this.selectedStudio.leadSetting) {
        return this.selectedStudio.leadSetting
      }
      return null
    },
    referralMail() {
      // Stellt sicher, dass params.email existiert, bevor replace aufgerufen wird
      return this.$route.params.email ? this.$route.params.email.replace("%40", "@") : ""
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
.lead-form-container {
  background-color: #f4f6f8;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Sorgt dafür, dass die E-Mail und Telefonnummern nicht in Grossbuchstaben sind */
::v-deep .lowercase .v-btn__content {
  text-transform: lowercase !important;
  font-size: 1rem;
}
</style>
